import type { BusinessDate } from '../date/business-date';
import { domainError, type DomainError } from '../shared/domain-error';
import { err, ok, type Result } from '../shared/result';
import type { DailyInspectionId, ExecutionResourceId, ProjectId, UserId } from '../shared/ids';
import { PLANNING_ERROR, PLANNING_ERROR_MESSAGE } from './errors';

/**
 * Conferência diária — um segundo par de olhos sobre o que foi planejado.
 *
 * Existe uma por `(recurso, dia)` (RN-100). Não é uma cópia do planejamento: as horas
 * continuam vivendo na alocação, e a conferência é **o ato** de olhar para elas e dizer o
 * que aconteceu — confirmar, ajustar, ou registrar que a pessoa não veio.
 *
 * ```text
 * 17/08/2026
 * Funcionário: João da Silva
 * Função: Pedreiro
 * Planejado: Alvenaria — 3º pavimento
 * Horas: 9h
 * ```
 *
 * Por que não existe uma tabela de "itens conferidos": ajustar na conferência altera **a
 * alocação**, e o histórico do que mudou fica em `PlanningChange` com `source =
 * INSPECTION`. Duplicar as linhas numa segunda tabela criaria dois lugares para a mesma
 * verdade, e o segundo é sempre o que fica desatualizado.
 *
 * O que a conferência guarda além disso é o que só ela sabe: **o planejado congelado** no
 * momento em que foi aberta, para que a Fase 10 compare planejado × executado sem
 * reconstruir o histórico; quem conferiu e quando; o motivo da ausência; e as fotos.
 */

export type InspectionStatus = 'PENDING' | 'CONFIRMED' | 'ADJUSTED' | 'ABSENT';

export const INSPECTION_STATUS_LABEL: Readonly<Record<InspectionStatus, string>> = {
  PENDING: 'Pendente',
  CONFIRMED: 'Confirmada',
  ADJUSTED: 'Ajustada',
  ABSENT: 'Ausente',
};

export interface DailyInspection {
  readonly id: DailyInspectionId;
  readonly projectId: ProjectId;
  readonly resourceId: ExecutionResourceId;
  readonly date: string;
  readonly status: InspectionStatus;
  /** Total planejado no instante em que a conferência foi aberta — congelado (Fase 10). */
  readonly plannedQuarterHours: number;
  /** Total executado. Ausente enquanto `PENDING`; zero quando `ABSENT` (RN-107). */
  readonly executedQuarterHours?: number;
  readonly absenceReason?: string;
  readonly notes?: string;
  /** Quem conferiu — a resposta da Q13 no nível da conferência (RN-101). */
  readonly inspectedByUserId?: UserId;
  readonly inspectedAt?: string;
  /** Quantas vezes foi reaberta. Reabrir é ação auditada, não apagada (RN-106). */
  readonly reopenCount: number;
  readonly version: number;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/** Chave natural: uma conferência por (recurso, dia) — RN-100. */
export function dailyInspectionNaturalKey(resourceId: ExecutionResourceId, date: string): string {
  return `${resourceId}|${date}`;
}

/**
 * Não se confere o futuro (RN-102).
 *
 * O "hoje" é o da obra, pelo fuso dela (RN-123) — conferir o dia seguinte porque o
 * servidor virou a data antes do canteiro seria registrar execução que não aconteceu.
 */
export function ensureNotFuture(
  date: BusinessDate,
  today: BusinessDate,
): Result<void, DomainError> {
  if (date.isAfter(today)) {
    return err(
      domainError(
        PLANNING_ERROR.INSPECTION_IN_FUTURE,
        PLANNING_ERROR_MESSAGE[PLANNING_ERROR.INSPECTION_IN_FUTURE]!,
        { date: date.toString(), today: today.toString() },
      ),
    );
  }
  return ok(undefined);
}

export function openInspection(input: {
  id: DailyInspectionId;
  projectId: ProjectId;
  resourceId: ExecutionResourceId;
  date: string;
  plannedQuarterHours: number;
  now: string;
}): DailyInspection {
  return {
    id: input.id,
    projectId: input.projectId,
    resourceId: input.resourceId,
    date: input.date,
    status: 'PENDING',
    plannedQuarterHours: input.plannedQuarterHours,
    reopenCount: 0,
    version: 1,
    createdAt: input.now,
    updatedAt: input.now,
  };
}

/** Conferência encerrada não recebe alteração sem reabrir antes (RN-106). */
export function isClosed(inspection: DailyInspection): boolean {
  return inspection.status !== 'PENDING';
}

function ensureOpen(inspection: DailyInspection): Result<void, DomainError> {
  if (!isClosed(inspection)) return ok(undefined);
  return err(
    domainError(
      PLANNING_ERROR.INSPECTION_CLOSED,
      PLANNING_ERROR_MESSAGE[PLANNING_ERROR.INSPECTION_CLOSED]!,
      { status: inspection.status },
    ),
  );
}

/**
 * Confirmação simples: o que foi planejado foi executado (RN-103).
 *
 * **Não gera alteração de planejamento** — não houve alteração nenhuma. É um ato próprio,
 * registrado com autor e instante, e é o caminho de longe mais comum: na maioria dos dias
 * o plano se cumpre, e o sistema não pode transformar isso em burocracia.
 */
export function confirmInspection(
  inspection: DailyInspection,
  by: UserId,
  now: string,
): Result<DailyInspection, DomainError> {
  const open = ensureOpen(inspection);
  if (!open.ok) return err(open.error);

  return ok({
    ...inspection,
    status: 'CONFIRMED',
    executedQuarterHours: inspection.plannedQuarterHours,
    inspectedByUserId: by,
    inspectedAt: now,
    version: inspection.version + 1,
    updatedAt: now,
  });
}

/**
 * Ajustada: alguma alocação do dia mudou durante a conferência (RN-104).
 *
 * O executado é recalculado a partir das alocações, não digitado: o que a pessoa fez é o
 * que está no plano depois do ajuste.
 *
 * Encerrada não recebe ajuste sem reabrir antes (RN-106), pela mesma razão que confirmar
 * e registrar ausência não recebem: passar por cima de uma conferência fechada trocaria o
 * ato auditado de reabrir por uma sobrescrita silenciosa — e uma ausência viraria
 * "ajustada" carregando o motivo da falta junto.
 */
export function markAdjusted(
  inspection: DailyInspection,
  executedQuarterHours: number,
  by: UserId,
  now: string,
): Result<DailyInspection, DomainError> {
  const open = ensureOpen(inspection);
  if (!open.ok) return err(open.error);

  return ok({
    ...inspection,
    status: 'ADJUSTED',
    executedQuarterHours,
    inspectedByUserId: by,
    inspectedAt: now,
    version: inspection.version + 1,
    updatedAt: now,
  });
}

/**
 * Ausência (RN-107).
 *
 * O planejado **permanece visível**: apagar as horas esconderia exatamente o que o
 * relatório precisa mostrar — que havia trabalho previsto e ele não aconteceu. O executado
 * vai a zero, e o motivo é obrigatório.
 */
export function registerAbsence(
  inspection: DailyInspection,
  reason: string,
  by: UserId,
  now: string,
): Result<DailyInspection, DomainError> {
  const open = ensureOpen(inspection);
  if (!open.ok) return err(open.error);

  const trimmed = reason.trim();
  if (trimmed.length < 3) {
    return err(
      domainError(
        PLANNING_ERROR.ABSENCE_REASON_REQUIRED,
        PLANNING_ERROR_MESSAGE[PLANNING_ERROR.ABSENCE_REASON_REQUIRED]!,
      ),
    );
  }

  return ok({
    ...inspection,
    status: 'ABSENT',
    executedQuarterHours: 0,
    absenceReason: trimmed,
    inspectedByUserId: by,
    inspectedAt: now,
    version: inspection.version + 1,
    updatedAt: now,
  });
}

/**
 * Reabre a conferência (RN-106).
 *
 * O contador de reaberturas sobe e nada é apagado: quem conferiu antes continua
 * registrado na auditoria, e a próxima confirmação vai por cima com autor novo. Reabrir é
 * um ato, não um desfazer.
 */
export function reopenInspection(
  inspection: DailyInspection,
  now: string,
): Result<DailyInspection, DomainError> {
  if (!isClosed(inspection)) {
    return err(
      domainError(
        PLANNING_ERROR.INSPECTION_ALREADY_OPEN,
        PLANNING_ERROR_MESSAGE[PLANNING_ERROR.INSPECTION_ALREADY_OPEN]!,
      ),
    );
  }

  const { executedQuarterHours: _executed, absenceReason: _reason, ...rest } = inspection;

  return ok({
    ...rest,
    status: 'PENDING',
    reopenCount: inspection.reopenCount + 1,
    version: inspection.version + 1,
    updatedAt: now,
  });
}
